import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContainedButton } from 'components/ContainedButton';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { login, redeemBootstrap } from 'services/auth';

import OnboardingLayout from '../../layouts/onboarding-layout/onboarding-layout';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    maxWidth: theme.spacing(60),
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  link: {
    color: theme.palette.secondary.main,
  },
}));

// confirm: waiting for the user gesture; retry: transient failure, same handle/token kept in memory; invalid: terminal.
type Phase = 'confirm' | 'busy' | 'retry' | 'invalid';

// `#h=<handle>` is the emailed bootstrap. `#error=not_yet|invalid` has no dedicated copy and reuses email-bootstrap-error.
const readHandle = (hash: string): string | null => {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  if (params.get('error')) return null;
  return params.get('h') || null;
};

const OAuthStart = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { hash, pathname } = useLocation();
  // Captured once, before the fragment is stripped below.
  const [handle] = useState(() => readHandle(hash));
  const [phase, setPhase] = useState<Phase>(handle ? 'confirm' : 'invalid');
  const inFlight = useRef(false);
  // The handle is single-use; after a successful redeem this in-memory token is the only copy.
  const bootstrapToken = useRef<string | null>(null);

  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
    history.replace(pathname);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startAuthorize = async (token: string): Promise<void> => {
    try {
      await login({ oauthToken: token });
    } catch {
      setPhase('retry');
      inFlight.current = false;
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (inFlight.current || !handle) return;
    inFlight.current = true;
    setPhase('busy');

    if (bootstrapToken.current) {
      await startAuthorize(bootstrapToken.current);
      return;
    }

    const result = await redeemBootstrap(handle);
    if (result.ok) {
      bootstrapToken.current = result.token;
      await startAuthorize(result.token);
      return;
    }
    inFlight.current = false;
    setPhase(result.code === 'invalid' ? 'invalid' : 'retry');
  };

  const busy = phase === 'busy';

  return (
    <OnboardingLayout>
      <Box className={classes.wrapper}>
        {phase === 'invalid' ? (
          <Paper className={classes.panel} role="alert">
            <Typography variant="body1">{t('auth:email-bootstrap-error')}</Typography>
            <Link to="/sign-in" className={classes.link}>
              <Typography variant="body2">{t('auth:go-login')}</Typography>
            </Link>
          </Paper>
        ) : (
          <Paper className={classes.panel} aria-busy={busy}>
            <Typography variant="h6">{t('auth:welcome')}</Typography>
            {busy && <Typography variant="body2">{t('auth:redirecting')}</Typography>}
            {phase === 'retry' && (
              <Typography variant="body2" color="error" role="alert">
                {t('auth:error-try-again')}
              </Typography>
            )}
            <ContainedButton
              text={t('auth:email-bootstrap-confirm')}
              icon="check"
              isLoading={busy}
              disabled={busy}
              onClick={handleConfirm}
            />
          </Paper>
        )}
      </Box>
    </OnboardingLayout>
  );
};

export default OAuthStart;
